import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import api from "../../../api/api";

const AddEmployeeModal = ({
  addEmployeeModalOpen,
  setAddEmployeeModalOpen,
  setEmployees,
}) => {
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const handleChange = (e) => {
    setEmployeeDetails((values) => ({
      ...values,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await api.post(
        "/business-owner/employee",
        employeeDetails
      );

      setEmployees((prevEmployees) => [...prevEmployees, response.data.user]);
      handleCancel();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.errMsg
      ) {
        setFormError(error.response.data.errMsg);
      }
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEmployeeDetails({});
    setAddEmployeeModalOpen(false);
  };

  return (
    <Dialog
      open={addEmployeeModalOpen}
      onClose={handleCancel}
      className="relative z-10"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div>
              <div className="sm:mt-0">
                <DialogTitle
                  as="h3"
                  className="text-center font-semibold text-gray-900"
                >
                  Add a new employee
                </DialogTitle>
                <div className="mt-2">
                  <form
                    onSubmit={handleSubmit}
                    method="POST"
                    className="space-y-6"
                  >
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm/6 font-medium text-gray-900"
                      >
                        First name
                      </label>
                      <div className="mt-2">
                        <input
                          onChange={handleChange}
                          id="firstName"
                          name="firstName"
                          type="text"
                          defaultValue={employeeDetails.firstName}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm/6 font-medium text-gray-900"
                      >
                        Last name
                      </label>
                      <div className="mt-2">
                        <input
                          onChange={handleChange}
                          id="lastName"
                          name="lastName"
                          type="text"
                          defaultValue={employeeDetails.lastName}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="username"
                        className="block text-sm/6 font-medium text-gray-900"
                      >
                        Username
                      </label>
                      <div className="mt-2">
                        <input
                          onChange={handleChange}
                          id="username"
                          name="username"
                          type="text"
                          defaultValue={employeeDetails.username}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm/6 font-medium text-gray-900"
                      >
                        Email
                      </label>
                      <div className="mt-2">
                        <input
                          onChange={handleChange}
                          id="email"
                          name="email"
                          type="email"
                          defaultValue={employeeDetails.email}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="phoneNumber"
                        className="block text-sm/6 font-medium text-gray-900"
                      >
                        Phone number
                      </label>
                      <div className="mt-2">
                        <input
                          onChange={handleChange}
                          id="phoneNumber"
                          name="phoneNumber"
                          type="text"
                          defaultValue={employeeDetails.phoneNumber}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="position"
                        className="block text-sm/6 font-medium text-gray-900"
                      >
                        Position
                      </label>
                      <div className="mt-2">
                        <input
                          onChange={handleChange}
                          id="position"
                          name="position"
                          type="text"
                          defaultValue={employeeDetails.position}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </div>

                    {
                        formError &&
                        <p id="email-error" className="mt-2 text-sm text-red-600">
                        {formError}
                        </p>
                    }

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={loading}
                        className={`inline-flex w-full justify-center rounded-md  px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3  sm:w-auto ${
                          loading
                            ? `bg-gray-400 cursor-not-allowed`
                            : `bg-indigo-600 hover:bg-indigo-500`
                        }`}
                      >
                        {loading ? "Loading..." : "Add"}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default AddEmployeeModal;
